import { ArPhotoButton } from '@/components/Pages/spots/components/ArPhotoButton'
import { AudioGuideButton } from '@/components/Pages/spots/components/AudioGuideButton'
import { SheetName } from '@/components/Pages/spots/components/SheetName'
import { TagBadge } from '@/components/Pages/spots/components/TagBadge'
import type { SpotData } from '@/schemas/v2/spot'
import { IconChevronRight } from '@tabler/icons-react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'

/** シート名バッジの表示件数上限（#3239）。超過分は「+N」バッジに集約する */
const SHEET_NAME_MAX_ITEMS = 2

type Props = {
  spot: SpotData
  /** スポット詳細ページへのパス（例: `/uji/spot/1`） */
  href: string
  /** タグ一覧（例: ['観光', '歴史', 'その他']）*/
  tags?: string[]
  /** 所属シート名一覧（例: ['京都観光コース', '歴史探索コース']） */
  sheetNames?: string[]
  /** 音声ガイドの有無 */
  hasAudioGuide?: boolean
  /** 音声ガイドボタン押下時のコールバック（スポット詳細と同様に DialogSpotsAudioGuide を開く） */
  onAudioGuideClick?: () => void
  /** 有効範囲外のとき音声ガイドボタンを鍵アイコンにする */
  audioGuideLocked?: boolean
  /** 音声ガイドがアクティブ（primaryカラー表示）かどうか */
  audioGuideActive?: boolean
  /** ARフォトの有無 */
  hasArPhoto?: boolean
  /** ARフォトボタン押下時のコールバック（スポット詳細と同様に ArPhotoOverlay を開く） */
  onArPhotoClick?: () => void
  /** スタンプ未取得または有効範囲外のとき ARフォトボタンを鍵アイコンにする */
  arPhotoLocked?: boolean
  /** ARフォトがアクティブ（primaryカラー表示）かどうか */
  arPhotoActive?: boolean
  /** スポット名を非表示にする（Drawerタイトルに表示済みの場合に使用） */
  hideName?: boolean
  /** 位置情報取得中はスケルトン表示にして disabled にする */
  geoLoading?: boolean
}

/**
 * /spots にて表示される次の場所にて利用される
 * - map状態の下から出てくるModal内のコンポーネント
 * - list状態のコンポーネント
 * */
export function SpotOverview({
  spot,
  href,
  tags,
  sheetNames,
  hasAudioGuide,
  onAudioGuideClick,
  audioGuideLocked = false,
  audioGuideActive = false,
  hasArPhoto,
  onArPhotoClick,
  arPhotoLocked = false,
  arPhotoActive = false,
  hideName = false,
  geoLoading = false,
}: Props) {
  const { t } = useTranslation()
  // min-h-[132px] はサムネイル 100px + py-4×2（32px）の下限。内容が上回る場合はカードが伸びる
  const cardClassName = 'flex items-start gap-3 px-4 py-4 min-h-[132px]'

  // シート名は先頭 SHEET_NAME_MAX_ITEMS 件のみ表示し、残りは「+N」バッジに集約する（#3239）
  const visibleSheetNames = (sheetNames ?? []).slice(0, SHEET_NAME_MAX_ITEMS)
  const overflowSheetCount = (sheetNames?.length ?? 0) - visibleSheetNames.length

  // ARフォト・音声ガイドボタン（ある場合のみ表示）
  const actionButtons = (hasArPhoto || hasAudioGuide) && (
    <div
      className="flex shrink-0 flex-wrap items-start gap-2"
      onClick={(e) => {
        // カード全体が Link / button のため、ボタン押下では遷移させない
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {hasArPhoto && (
        <ArPhotoButton active={arPhotoActive} locked={arPhotoLocked} loading={geoLoading} onClick={onArPhotoClick} />
      )}
      {hasAudioGuide && (
        <AudioGuideButton active={false} locked={audioGuideLocked} loading={geoLoading} onClick={onAudioGuideClick} />
      )}
    </div>
  )

  // クーポンバッジ（クーポンが設定されている場合のみ表示）
  const couponBadge = spot.coupon && (
    <div className="flex flex-wrap gap-1">
      <span className="rounded-[8px] border border-[#ff8a00] px-2 py-0.5 text-[12px] font-bold leading-[16px] text-black">
        {t('spot_detail.coupon', 'クーポン')}
      </span>
    </div>
  )

  // タグバッジ
  const tagBadges = tags && tags.length > 0 && (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag, idx) => (
        <TagBadge key={`${tag}-${idx}`} tag={tag} />
      ))}
    </div>
  )

  const content = (
    <>
      {/* サムネイル */}
      <div className="relative size-[100px] shrink-0 self-start">
        {spot.images[0] ? (
          <img src={spot.images[0]} alt={spot.name} className="size-full rounded-[16px] object-cover" />
        ) : (
          <div className="size-full rounded-[16px] bg-gray-100" aria-hidden="true" />
        )}
      </div>

      {/* テキスト情報。縦間隔は gap-1（4px）に一元化し、子要素に個別の margin を持たせない */}
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        {/* スポット名（Drawer ではヘッダーに表示するため非表示） */}
        {!hideName && <p className="text-[16px] font-bold leading-[24px] text-black line-clamp-2">{spot.name}</p>}

        {/*
          シート名バッジ（テキスト列の横幅いっぱいを使う）。
          文字数での切り詰めはせず、列幅に収まらない場合のみ CSS で 1 行省略（…）する。
        */}
        {visibleSheetNames.length > 0 && (
          <div className="flex w-full min-w-0 flex-wrap gap-1">
            {visibleSheetNames.map((name, idx) => (
              <SheetName key={`${name}-${idx}`} name={name} truncate />
            ))}
            {/* 表示上限を超えた分は件数バッジに集約する（言語非依存のため i18n キー不要） */}
            {overflowSheetCount > 0 && <SheetName name={`+${overflowSheetCount}`} />}
          </div>
        )}

        {/*
          シート名の下段。リスト・Drawer 共通で横並びにする。
          左: クーポン・タグを縦積み / 右: ARカメラ・音声ガイドボタン
        */}
        {(couponBadge || tagBadges || actionButtons) && (
          <div className="flex w-full items-start justify-between gap-2">
            <div className="min-w-0 flex flex-1 flex-col gap-1">
              {couponBadge}
              {tagBadges}
            </div>
            {actionButtons}
          </div>
        )}
      </div>

      {/* 矢印（右端） */}
      <IconChevronRight size={24} stroke={1.5} className="shrink-0 self-center text-gray-400" aria-hidden="true" />
    </>
  )

  return (
    <Link href={href} className={cardClassName}>
      {content}
    </Link>
  )
}
